import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { isStaff, useAuth } from "./auth";
import { actorIds, lastChangedLabel } from "./inputStamps.mjs";
import type { InputStamp } from "./inputStamps.d.mts";
import { getSupabase } from "./supabase";

/**
 * Settings > Inputs: names for "who last changed this".
 *
 * Each stored input carries its writer's user id (inputStamps.mjs). The
 * provider turns the ids the page shows into names, once each, through
 * public.report_edit_actor_names (migration 0105): the database answers only
 * for a session that can see the tenant, and only for that tenant's members
 * (non-member Meridian staff read as "Meridian staff"). Nothing outside the
 * tenant is ever named.
 *
 * Until 0105 is applied the function is not there. The labels then stay
 * true without it ("you", "Meridian", "another user"), and a staff session
 * falls back to admin_list_tenant_users (0044, staff only) so staff reviewing
 * the tab still read names.
 *
 * A component outside the provider (the staff panel on Cost & Disposal) gets
 * null from useInputActors and draws no "Last changed" column.
 */

export interface InputActors {
  /** "Oct 1, 2026, 2:42 PM by Zay"; null when there is no stamp. */
  label: (stamp: InputStamp | null | undefined) => string | null;
  /** Ask for the names behind these stamps; they arrive in the background. */
  want: (stamps: ReadonlyArray<InputStamp | null | undefined>) => void;
  /**
   * A writer's name for print (the AR write-off report's notes): the
   * tenant member's name, the reader's own name for the reader; null for a
   * row with no writer and while a name is not known.
   */
  name: (id: string | null | undefined) => string | null;
}

const InputActorsContext = createContext<InputActors | null>(null);

/** Collect ids asked for within one tick into a single request. */
const BATCH_MS = 40;

/**
 * Set once the database has answered that report_edit_actor_names is not
 * there (0105 not applied yet), so the page asks once per load, not once per
 * section.
 */
let namesFunctionMissing = false;

/** PostgREST's answer for a function it does not have (PGRST202, HTTP 404). */
function isFunctionMissing(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === "PGRST202" || /could not find the function/i.test(error.message ?? "");
}

async function fetchActorNames(
  tenantId: string,
  ids: string[],
  staff: boolean,
): Promise<Map<string, string>> {
  const supabase = getSupabase();
  const out = new Map<string, string>();
  if (!namesFunctionMissing) {
    const named = await supabase.rpc("report_edit_actor_names", {
      p_tenant: tenantId,
      p_user_ids: ids,
    });
    if (!named.error && Array.isArray(named.data)) {
      for (const r of named.data as { user_id?: string; display_name?: string }[]) {
        if (r.user_id && r.display_name) out.set(r.user_id, r.display_name);
      }
      return out;
    }
    if (isFunctionMissing(named.error)) namesFunctionMissing = true;
  }
  if (!staff) return out;
  const members = await supabase.rpc("admin_list_tenant_users", { p_tenant_id: tenantId });
  if (!members.error && Array.isArray(members.data)) {
    for (const r of members.data as { user_id?: string; user_name?: string; email?: string }[]) {
      const name = r.user_name || r.email;
      if (r.user_id && name && ids.includes(r.user_id)) out.set(r.user_id, name);
    }
  }
  return out;
}

/** Mount with `key={tenantId}`: a provider names one tenant's writers only. */
export function InputActorsProvider(props: { tenantId: string | null; children: ReactNode }) {
  const { tenantId, children } = props;
  const { session } = useAuth();
  const selfId = session?.user.id ?? null;
  const meta = (session?.user.user_metadata ?? {}) as Record<string, unknown>;
  const selfName =
    [meta.full_name, meta.name, meta.user_name, session?.user.email].find(
      (v): v is string => typeof v === "string" && v.trim() !== "",
    ) ?? null;
  const staff = isStaff(session);
  const [names, setNames] = useState<Map<string, string>>(() => new Map());
  // Ids already asked for (answered or not) and ids waiting for the next
  // request. One provider serves one tenant: the caller keys it by tenant id,
  // so another tenant gets a fresh provider and none of these names.
  const asked = useRef(new Set<string>());
  const waiting = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  const want = useCallback(
    (stamps: ReadonlyArray<InputStamp | null | undefined>) => {
      if (!tenantId) return;
      for (const id of actorIds(stamps)) {
        if (id === selfId || asked.current.has(id)) continue;
        asked.current.add(id);
        waiting.current.add(id);
      }
      if (waiting.current.size === 0 || timer.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        const ids = [...waiting.current];
        waiting.current = new Set();
        if (ids.length === 0) return;
        fetchActorNames(tenantId, ids, staff).then(
          (found) => {
            if (!alive.current || found.size === 0) return;
            setNames((prev) => new Map([...prev, ...found]));
          },
          () => undefined,
        );
      }, BATCH_MS);
    },
    [tenantId, selfId, staff],
  );

  const value = useMemo<InputActors>(
    () => ({
      label: (stamp) => lastChangedLabel(stamp, { selfId, names }),
      want,
      name: (id) => {
        // No writer on the row (a note carried over from the client's page):
        // unnamed, never a guess.
        if (!id) return null;
        if (id === selfId) return selfName;
        return names.get(id) ?? null;
      },
    }),
    [selfId, selfName, names, want],
  );
  return createElement(InputActorsContext.Provider, { value }, children);
}

/** The provider's labeler, or null outside Settings > Inputs. */
export function useInputActors(): InputActors | null {
  return useContext(InputActorsContext);
}

/**
 * Ask for the names behind `stamps` (memoise the array) and get the labeler.
 * Null outside the provider.
 */
export function useStampLabels(
  stamps: ReadonlyArray<InputStamp | null | undefined>,
): InputActors["label"] | null {
  const actors = useInputActors();
  const want = actors?.want;
  useEffect(() => {
    want?.(stamps);
  }, [want, stamps]);
  return actors ? actors.label : null;
}
